import { useQuery } from '@apollo/client';
import * as _ from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import * as styles from './TopicPosts.module.scss';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import InfiniteScrollWaypoint from '../../components/InfiniteScrollWaypoint';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import {
  TopicDetached,
  TopicPostsOrder,
  TopicPostsType,
} from '../../graphql/generated/graphql';
import allPostsIcon from '../../icons/non-prefilled/16x16/allPosts.svg';
import archiveIcon from '../../icons/non-prefilled/16x16/archive.svg';
import draftsIcon from '../../icons/non-prefilled/16x16/drafts.svg';
import orderIcon from '../../icons/non-prefilled/16x16/order.svg';
import postIcon from '../../icons/non-prefilled/16x16/post.svg';
import templateIcon from '../../icons/non-prefilled/16x16/template.svg';
import verifiedIcon from '../../icons/non-prefilled/16x16/verified.svg';
import PostFilterMenu from '../../post/PostFilterMenu';
import { UIActions } from '../../ui/uiSlice';
import { isFeatureEnabled } from '../../utils/features';
import ConnectionHelper from '../../utils/graphql/ConnectionHelper';
import TopicListDefaultsSettings from '../TopicListDefaultsSettings';
import type { EditTopicState } from '../TopicView';
import SubtopicPostsSection, {
  SUBTOPIC_POSTS_INITIAL_PAGE_SIZE,
} from './SubtopicPostsSection';
import TopicPostSection from './TopicPostSection';
import usePinMutations from './usePinMutations';

const DEFAULT_POSTS_TYPE = TopicPostsType.Published;
const DEFAULT_POSTS_ORDER_BY = TopicPostsOrder.Popularity;

const TOPIC_POSTS_QUERY = gql(/** @complexity 35 */ `
  query topicPosts(
    $afterCursor: String
    $id: ID!
    $orderBy: TopicPostsOrder
    $type: TopicPostsType
  ) {
    topic(id: $id) {
      id
      postCounts {
        type
        count
      }
      posts(after: $afterCursor, first: 20, orderBy: $orderBy, type: $type) {
        ...TopicPostSection_posts
      }
    }
  }
`);

const TOPIC_DESCENDANTS_QUERY = gql(/** @complexity 77 */ `
  query topicDescendants(
    $afterCursor: String
    $id: ID!
    $postsFirst: Int = 5
    $postsAfterCursor: String
    $orderBy: TopicPostsOrder
    $type: TopicPostsType
  ) {
    topic(id: $id) {
      id
      descendants(after: $afterCursor, first: 10) {
        edges {
          node {
            id
            detached
            ...SubtopicPostsSection_topic
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`);

const TOPIC_POSTS_DATA_TOPIC_FRAGMENT = gql(`
  fragment useTopicPostsData_topic on Topic {
    id
    defaultShow
    defaultSort
  }
`);

const useTopicPostsData = (
  topic: DocumentType<typeof TOPIC_POSTS_DATA_TOPIC_FRAGMENT>,
) => {
  const [type, setType] = useState<TopicPostsType>(
    topic.defaultShow ?? DEFAULT_POSTS_TYPE,
  );

  const orderBy =
    useAppSelector((state) => state.ui.topicPostsOrderBy[topic.id]) ??
    topic.defaultSort ??
    DEFAULT_POSTS_ORDER_BY;

  useEffect(() => {
    if (topic.defaultShow) {
      setType(topic.defaultShow);
    }
  }, [topic.defaultShow]);

  const topicPostsQueryResponse = useQuery(TOPIC_POSTS_QUERY, {
    variables: {
      id: topic.id,
      orderBy,
      type,
    },
  });

  const fetchMorePosts = ConnectionHelper.generateFetchMore(
    TOPIC_POSTS_QUERY,
    topicPostsQueryResponse,
    'topic.posts',
  );

  const isDescendantsQuerySkipped = Boolean(fetchMorePosts);

  const topicDescendantsResponse = useQuery(TOPIC_DESCENDANTS_QUERY, {
    skip: isDescendantsQuerySkipped,
    variables: {
      id: topic.id,
      orderBy,
      type,
      postsFirst: SUBTOPIC_POSTS_INITIAL_PAGE_SIZE,
    },
  });
  const fetchMoreDescendants = ConnectionHelper.generateFetchMore(
    TOPIC_DESCENDANTS_QUERY,
    topicDescendantsResponse,
    'topic.descendants',
  );

  const refetch = () => {
    topicPostsQueryResponse.refetch();
    topicDescendantsResponse.refetch();
  };

  const currentTopic =
    topicPostsQueryResponse.data &&
    (topicDescendantsResponse.data || isDescendantsQuerySkipped)
      ? {
          ...topicPostsQueryResponse.data.topic,
          posts: topicPostsQueryResponse.data.topic.posts,
          postCounts: topicPostsQueryResponse.data.topic.postCounts,
          ...topicDescendantsResponse.data?.topic,
          descendants: topicDescendantsResponse.data?.topic?.descendants,
        }
      : undefined;

  const currentOrLastTopic = useRef<typeof currentTopic>(currentTopic);
  if (currentTopic) {
    currentOrLastTopic.current = currentTopic;
  }

  return {
    type,
    orderBy,
    setType,
    topic: currentOrLastTopic.current,
    fetchMorePosts,
    fetchMoreDescendants,
    isLoading: !currentTopic,
    refetch,
  };
};

const TOPIC_FRAGMENT = gql(`
  fragment TopicPosts_topic on Topic {
    id
    ...useTopicPostsData_topic
  }
`);

interface BaseTopicPostsProps {
  topic: DocumentType<typeof TOPIC_FRAGMENT>;
  editing: boolean;
  editTopicState: (EditTopicState & { touched: boolean }) | null;
  updateEditTopicState: (payload: Partial<EditTopicState | null>) => void;
}

const TopicPosts = (props: BaseTopicPostsProps) => {
  const { editing: isEditing, editTopicState, updateEditTopicState } = props;
  const dispatch = useAppDispatch();
  const topicId = props.topic.id;

  const {
    type,
    orderBy,
    setType,
    topic,
    fetchMorePosts,
    fetchMoreDescendants,
    isLoading,
    refetch,
  } = useTopicPostsData(props.topic);

  const { pinPost, unpinPost } = usePinMutations({
    topicId,
    type,
    orderBy,
    postsCount: _.compact(topic?.posts?.edges).length,
  });

  const postCounts = topic?.postCounts ?? [];
  const posts = topic?.posts;
  const postCountByType = postCounts.reduce<Record<TopicPostsType, number>>(
    (counts, { type: postType, count }) =>
      postType && count ? { ...counts, [postType]: count } : counts,
    {
      [TopicPostsType.All]: 0,
      [TopicPostsType.Archived]: 0,
      [TopicPostsType.Draft]: 0,
      [TopicPostsType.Published]: 0,
      [TopicPostsType.Verified]: 0,
      [TopicPostsType.Template]: 0,
    },
  );

  const hasDirectPosts = posts?.edges && posts.edges.length > 0;

  const descendants = _.compact(
    topic?.descendants?.edges
      ?.filter((edge) => (edge?.node?.posts?.edges?.length ?? 0) > 0)
      .map((edge) => edge?.node) ?? [],
  )
    .filter(
      ({ detached }) =>
        !isFeatureEnabled('detached_topics') || detached !== TopicDetached.All,
    )
    .slice(0, isLoading ? 1 : undefined); // Show only first descendant while loading

  const isShowingDescendants =
    descendants.length > 0 && (!isLoading || !hasDirectPosts);

  const postSectionProps = {
    orderBy,
    topicId,
    refetch,
    controls:
      isEditing && isFeatureEnabled('topic_sort') && editTopicState ? (
        <TopicListDefaultsSettings
          editTopicState={editTopicState}
          updateEditTopicState={updateEditTopicState}
          postCounts={postCountByType}
        />
      ) : (
        <>
          <PostFilterMenu<TopicPostsType>
            onChange={setType}
            options={{
              [TopicPostsType.Published]: {
                icon: postIcon,
                count: postCountByType.PUBLISHED,
                label: 'Published',
              },
              [TopicPostsType.Verified]: {
                icon: verifiedIcon,
                count: postCountByType.VERIFIED,
                label: 'Verified',
              },
              [TopicPostsType.Template]: {
                icon: templateIcon,
                count: postCountByType.TEMPLATE,
                label: 'Template',
              },
              [TopicPostsType.Archived]: {
                icon: archiveIcon,
                count: postCountByType.ARCHIVED,
                label: 'Archived',
              },
              [TopicPostsType.Draft]: {
                icon: draftsIcon,
                count: postCountByType.DRAFT,
                label: 'Drafts',
              },
              [TopicPostsType.All]: {
                icon: allPostsIcon,
                count: postCountByType.ALL,
                label: 'All posts',
              },
            }}
            title="Show"
            value={type}
          />
          <PostFilterMenu
            icon={orderIcon}
            onChange={(order) =>
              dispatch(
                UIActions.changeTopicPostsOrderBy({ topicId, orderBy: order }),
              )
            }
            options={{
              [TopicPostsOrder.Popularity]: { label: 'Popularity' },
              [TopicPostsOrder.PublishedAt]: { label: 'Recently published' },
              [TopicPostsOrder.UpdatedAt]: { label: 'Recently updated' },
              [TopicPostsOrder.ViewedAt]: { label: 'Recently viewed' },
              [TopicPostsOrder.Title]: { label: 'Alphabetical' },
            }}
            menuPlacement="bottom-end"
            title="Sort"
            value={orderBy}
          />
        </>
      ),
  };

  return (
    <div className={isEditing ? styles.isEditing : undefined}>
      <TopicPostSection
        {...postSectionProps}
        loading={isLoading}
        fetchMore={fetchMorePosts}
        main={!!hasDirectPosts || !isShowingDescendants}
        pinPost={pinPost}
        posts={posts}
        title={!hasDirectPosts && isShowingDescendants ? null : 'Posts'}
        unpinPost={unpinPost}
      />
      {isShowingDescendants &&
        descendants.map((descendant, index) => (
          <SubtopicPostsSection
            key={descendant.id}
            {...postSectionProps}
            loading={isLoading}
            controls={
              !hasDirectPosts && index === 0 ? postSectionProps.controls : null
            }
            main={!hasDirectPosts && index === 0}
            topic={descendant}
            orderBy={orderBy}
            type={type}
          />
        ))}
      {!fetchMorePosts && (
        <InfiniteScrollWaypoint fetchMore={fetchMoreDescendants} />
      )}
    </div>
  );
};

export default TopicPosts;
